import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Button, Input, Label, Combobox,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui'
import {
  registrarPrestamo, getInventoryOverview, getAllStaff, getAllPlayersWithGroups,
} from '../../../lib/supabase'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'

const TIPO_OPTIONS = [
  { value: 'prestamo', label: 'Préstamo' },
  { value: 'asignacion', label: 'Asignación' },
]

const PRESTADO_A_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'jugador', label: 'Jugador' },
  { value: 'externo', label: 'Externo' },
]

const initialForm = {
  item_id: '',
  cantidad: '1',
  tipo: 'prestamo',
  prestado_a_tipo: 'staff',
  prestado_a_id: '',
  prestado_a_nombre: '',
  notas: '',
}

const PrestamoFormModal = ({ open, onOpenChange, onSuccess, defaultValues = null, title = 'Nuevo préstamo' }) => {
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const [form, setForm] = useState(initialForm)
  const [items, setItems] = useState([])
  const [staff, setStaff] = useState([])
  const [players, setPlayers] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const loadOptions = useCallback(async () => {
    // A quien se le puede prestar: solo quien sigue en el club (membresia activa, getAllStaff). Los nombres de los prestamos que
    // ya existen salen de InventarioPrestamos (getAllStaffIncludingInactive).
    const [overview, staffRes, playersRes] = await Promise.all([
      getInventoryOverview(),
      getAllStaff(),
      getAllPlayersWithGroups(),
    ])
    setItems(overview.data || [])
    setStaff(staffRes.data || [])
    setPlayers(playersRes.data || [])
  }, [])

  useEffect(() => {
    if (open) {
      loadOptions()
      setForm({ ...initialForm, ...(defaultValues || {}) })
      setError(null)
      guard.resetDirty()
    }
  }, [open, loadOptions, defaultValues]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSelectChange = (field) => (value) => {
    guard.markDirty()
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleTipoPrestadoChange = (value) => {
    guard.markDirty()
    setForm((prev) => ({ ...prev, prestado_a_tipo: value, prestado_a_id: '', prestado_a_nombre: '' }))
  }

  const esExterno = form.prestado_a_tipo === 'externo'
  const personas = form.prestado_a_tipo === 'staff' ? staff : players

  const itemOptions = items.map((it) => {
    const detalle = [it.talla, it.modelo].filter(Boolean).join(' · ')
    return {
      value: it.id,
      label: `${it.nombre}${detalle ? ` (${detalle})` : ''} · ${it.disponibles} disp.`,
    }
  })

  const personaOptions = personas.map((p) => ({ value: p.id, label: p.nombre_completo }))

  // Aviso suave (no bloquea): se permite prestar mas de lo disponible, solo se marca.
  const selectedItem = items.find((it) => it.id === form.item_id)
  const excedeStock = selectedItem && Number(form.cantidad) > Number(selectedItem.disponibles)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!form.item_id) {
      setError('Selecciona un artículo')
      return
    }
    if (esExterno) {
      if (!form.prestado_a_nombre.trim()) {
        setError('Escribe el nombre de la persona externa')
        return
      }
    } else if (!form.prestado_a_id) {
      setError('Selecciona a quién se presta')
      return
    }
    const cantidad = Number(form.cantidad)
    if (!cantidad || cantidad <= 0) {
      setError('Captura una cantidad mayor a cero')
      return
    }

    setSaving(true)
    const { error: saveError } = await registrarPrestamo({
      itemId: form.item_id,
      cantidad,
      tipo: form.tipo,
      prestadoATipo: form.prestado_a_tipo,
      prestadoAId: esExterno ? null : form.prestado_a_id,
      prestadoANombre: esExterno ? form.prestado_a_nombre.trim() : null,
      notas: form.notas.trim() || null,
    })
    setSaving(false)
    if (saveError) {
      setError(saveError.message || 'No se pudo registrar el préstamo')
      return
    }
    guard.resetDirty()
    onSuccess?.()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="prestamo-item">Artículo *</Label>
            <Combobox
              id="prestamo-item"
              className="w-full"
              options={itemOptions}
              value={form.item_id}
              onValueChange={handleSelectChange('item_id')}
              placeholder="Seleccionar artículo"
              searchPlaceholder="Buscar artículo..."
              emptyMessage="No hay artículos."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prestamo-cantidad">Cantidad</Label>
              <Input
                id="prestamo-cantidad"
                type="number"
                min="1"
                value={form.cantidad}
                onChange={handleChange('cantidad')}
                placeholder="1"
                required
              />
              {excedeStock && (
                <p className="text-xs text-amber-600">
                  Excede lo disponible ({selectedItem.disponibles}). Se registrará de todos modos.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="prestamo-tipo">Tipo</Label>
              <Select value={form.tipo} onValueChange={handleSelectChange('tipo')}>
                <SelectTrigger id="prestamo-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPO_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prestamo-a-tipo">Prestado a</Label>
              <Select value={form.prestado_a_tipo} onValueChange={handleTipoPrestadoChange}>
                <SelectTrigger id="prestamo-a-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRESTADO_A_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="prestamo-persona">
                {esExterno ? 'Nombre del externo' : form.prestado_a_tipo === 'staff' ? 'Miembro del staff' : 'Jugador'}
              </Label>
              {esExterno ? (
                <Input
                  id="prestamo-persona"
                  value={form.prestado_a_nombre}
                  onChange={handleChange('prestado_a_nombre')}
                  placeholder="Ej: Juan Pérez (otro club)"
                />
              ) : (
                <Combobox
                  id="prestamo-persona"
                  className="w-full"
                  options={personaOptions}
                  value={form.prestado_a_id}
                  onValueChange={handleSelectChange('prestado_a_id')}
                  placeholder="Seleccionar"
                  searchPlaceholder={form.prestado_a_tipo === 'staff' ? 'Buscar staff...' : 'Buscar jugador...'}
                  emptyMessage="Sin resultados."
                />
              )}
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="prestamo-notas">Notas</Label>
              <Input
                id="prestamo-notas"
                value={form.notas}
                onChange={handleChange('notas')}
                placeholder="Opcional"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando...' : 'Registrar préstamo'}
            </Button>
          </DialogFooter>
        </form>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default PrestamoFormModal
